import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  apiService,
  Connector,
  DebounceInterval,
  getApiErrorMessage,
  Index,
  Prompt,
} from '../services/api';
import {
  isRetryableStreamRequestError,
} from '../services/idempotency';
import {
  clearPendingIdempotencyAttempt,
  findPendingIdempotencyAttempt,
  getOrCreatePendingIdempotencyAttempt,
  PendingIdempotencyAttempt,
} from '../services/pendingIdempotencyAttempt';
import {
  AutomationMessage,
  AutomationDirtyDraftsByIndex,
  AutomationIndexDirtyDrafts,
  buildImportAutomationPromptPreset,
  createDefaultExportAutomationDraft,
  createDefaultImportAutomationDraft,
  ExportAutomationDraft,
  getImportAutomationDraftContractError,
  getAutomationMutationMessage,
  ImportAutomationDraft,
  isAutomationDebounceInterval,
  isAutomationPaused,
  normalizeExportDestinationSubpath,
  toExportAutomationDraft,
  toImportAutomationDraft,
} from '../utils/automationRuntime';
import { sortIndexes } from '../utils/indexSort';
import { usePassiveApiActivity } from './usePassiveApiActivity';
import { useEngineCatalog } from './useEngineCatalog';

type AutomationKind = 'import' | 'export';
export type MutationAttemptChannel = 'save' | 'status';

interface PendingMutationReconciliation {
  attemptChannel: MutationAttemptChannel;
  attemptSignature: string;
  baselineSignature: string;
  expectedSignature: string;
  successMessage: string;
  failureMessage: string;
}

interface MutationErrorObservation {
  baselineSignature: string;
  expectedSignature: string;
  successMessage: string;
  failureMessage: string;
}

interface AutomationLoadOptions {
  preserveDirty?: boolean;
  preserveDraft?: boolean;
  background?: boolean;
  mutationErrorObservation?: MutationErrorObservation;
}

export interface AutomationWorkspacePersistenceOptions {
  enabled?: boolean;
  isActive?: boolean;
  initialDirtyDraftsByIndex?: AutomationDirtyDraftsByIndex;
  loadImportReferences?: boolean;
  loadConnectorReferences?: boolean;
  onDirtyDraftsChange?: (
    indexId: string,
    drafts: AutomationIndexDirtyDrafts | null,
  ) => void;
}

interface ReferenceErrors {
  indexes: string | null;
  connectors: string | null;
}

export interface AutomationWorkspaceResult {
  eligibleIndexes: Index[];
  selectedIndex: Index | null;
  importConnectors: Connector[];
  exportConnectors: Connector[];
  prompts: Prompt[];
  importDraft: ImportAutomationDraft;
  exportDraft: ExportAutomationDraft;
  savedImportDraft: ImportAutomationDraft | null;
  savedExportDraft: ExportAutomationDraft | null;
  setImportDraft: React.Dispatch<React.SetStateAction<ImportAutomationDraft>>;
  setExportDraft: React.Dispatch<React.SetStateAction<ExportAutomationDraft>>;
  importDirty: boolean;
  exportDirty: boolean;
  importValidationError: string | null;
  exportValidationError: string | null;
  pendingImportMutation: MutationAttemptChannel | null;
  pendingExportMutation: MutationAttemptChannel | null;
  isLoadingReferences: boolean;
  isLoadingIndexes: boolean;
  isLoadingConnectors: boolean;
  isLoadingEngines: boolean;
  isLoadingImport: boolean;
  isLoadingExport: boolean;
  hasLoadedImport: boolean;
  hasLoadedExport: boolean;
  isSavingImport: boolean;
  isSavingExport: boolean;
  indexesError: string | null;
  connectorsError: string | null;
  enginesError: string | null;
  referencesError: string | null;
  importError: string | null;
  exportError: string | null;
  importMessage: AutomationMessage | null;
  exportMessage: AutomationMessage | null;
  refresh: () => Promise<void>;
  retryImport: () => Promise<void>;
  retryExport: () => Promise<void>;
  saveImport: () => Promise<boolean>;
  saveExport: () => Promise<boolean>;
  toggleImportPause: () => Promise<boolean>;
  toggleExportPause: () => Promise<boolean>;
  resetImportDraft: () => void;
  resetExportDraft: () => void;
}

// Normal mutations normally settle within the first few reads. The capped
// five-minute tail also spans the backend's one-hour stale-provisioning grace
// plus its five-minute maintenance cadence without aggressive or unbounded
// polling. Hidden pages do not consume attempts.
const PROVISIONING_RECONCILE_DELAYS_MS = [
  1500,
  3000,
  5000,
  8000,
  12000,
  30000,
  60000,
  120000,
  300000,
] as const;
const MAX_PROVISIONING_RECONCILE_ATTEMPTS = 22;
const MAX_RETAINED_MUTATION_ATTEMPTS = 32;
const AUTOMATION_ATTEMPT_TTL_MS = 24 * 60 * 60 * 1000;

const getAutomationAttemptStorage = (): Storage | null => {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
};

interface RetainedAutomationAttempt extends PendingIdempotencyAttempt {
  namespace: string;
}

const getProvisioningReconcileDelay = (attempt: number): number | null => {
  if (attempt < 0 || attempt >= MAX_PROVISIONING_RECONCILE_ATTEMPTS) return null;
  return PROVISIONING_RECONCILE_DELAYS_MS[
    Math.min(attempt, PROVISIONING_RECONCILE_DELAYS_MS.length - 1)
  ];
};

const scopesForConnector = (connector: Connector): string[] => (
  Array.isArray(connector.scopes) && connector.scopes.length > 0
    ? connector.scopes
    : ['import']
);

const isImportAutomationConnector = (connector: Connector): boolean => (
  connector.status === 'active'
  && (connector.provider === 'gcs' || connector.provider === 's3')
  && scopesForConnector(connector).includes('import')
);

const isExportAutomationConnector = (connector: Connector): boolean => (
  connector.status === 'active'
  && scopesForConnector(connector).includes('export')
);

const editableImportSignature = (draft: ImportAutomationDraft): string => JSON.stringify({
  enabled: draft.enabled,
  paused: draft.paused,
  connector_id: draft.connector_id,
  source_prefix: draft.source_prefix,
  file_pattern: draft.file_pattern,
  recursive: draft.recursive,
  debounce_interval: draft.debounce_interval,
  prompt_preset: {
    prompt_id: draft.prompt_id,
  },
});

const editableExportSignature = (draft: ExportAutomationDraft): string => JSON.stringify({
  enabled: draft.enabled,
  paused: draft.paused,
  destination_connector_id: draft.destination_connector_id,
  destination_subpath: draft.destination_subpath,
});

const persistableImportDraft = (draft: ImportAutomationDraft): ImportAutomationDraft => ({
  ...draft,
  runtime_status: null,
  runtime_error: null,
  next_execution_at: null,
  next_execution_error: null,
});

const persistableExportDraft = (draft: ExportAutomationDraft): ExportAutomationDraft => ({
  ...draft,
  runtime_status: null,
  runtime_error: null,
});

const buildImportMutationPayload = (draft: ImportAutomationDraft) => ({
  enabled: draft.enabled,
  paused: draft.enabled ? draft.paused : false,
  connector_id: draft.connector_id,
  source_prefix: draft.source_prefix,
  file_pattern: draft.file_pattern || '*',
  recursive: draft.recursive,
  debounce_interval: draft.debounce_interval as DebounceInterval,
  prompt_preset: buildImportAutomationPromptPreset(draft),
});

const buildExportMutationPayload = (draft: ExportAutomationDraft) => {
  const destinationSubpath = normalizeExportDestinationSubpath(draft.destination_subpath);
  return {
    enabled: draft.enabled,
    paused: draft.enabled ? draft.paused : false,
    destination_connector_id: draft.destination_connector_id,
    destination_subpath: destinationSubpath.value || undefined,
  };
};

const clearDurableAutomationAttempt = async (
  namespace: string,
  operation: string,
  requestPayload: unknown,
): Promise<void> => {
  const attempt = await findPendingIdempotencyAttempt({
    namespace,
    operation,
    requestPayload,
    storage: getAutomationAttemptStorage(),
  });
  if (!attempt) return;
  clearPendingIdempotencyAttempt({
    namespace,
    operation,
    requestFingerprint: attempt.requestFingerprint,
    expectedIdempotencyKey: attempt.idempotencyKey,
    storage: getAutomationAttemptStorage(),
  });
};

const importAutomationSnapshotSignature = (
  draft: ImportAutomationDraft | null,
): string => JSON.stringify(draft ? {
  editable: editableImportSignature(draft),
  runtime_status: draft.runtime_status ?? null,
  runtime_error: draft.runtime_error ?? null,
} : null);

const exportAutomationSnapshotSignature = (
  draft: ExportAutomationDraft | null,
): string => JSON.stringify(draft ? {
  editable: editableExportSignature(draft),
  runtime_status: draft.runtime_status ?? null,
  runtime_error: draft.runtime_error ?? null,
} : null);

const automationAttempt = async (
  retained: React.MutableRefObject<Map<string, RetainedAutomationAttempt>>,
  signature: string,
  prefix: string,
  namespace: string,
  operation: string,
  requestPayload: unknown,
): Promise<RetainedAutomationAttempt> => {
  const existingAttempt = retained.current.get(signature);
  if (existingAttempt) {
    // Refresh insertion order so actively retried operations are evicted last.
    retained.current.delete(signature);
    retained.current.set(signature, existingAttempt);
  }
  const attempt = await getOrCreatePendingIdempotencyAttempt({
    namespace,
    operation,
    requestPayload,
    keyPrefix: prefix,
    inMemoryAttempt: existingAttempt,
    storage: getAutomationAttemptStorage(),
    ttlMs: AUTOMATION_ATTEMPT_TTL_MS,
  });
  const retainedAttempt = { ...attempt, namespace };
  while (retained.current.size >= MAX_RETAINED_MUTATION_ATTEMPTS) {
    const oldestSignature = retained.current.keys().next().value;
    if (typeof oldestSignature !== 'string') break;
    retained.current.delete(oldestSignature);
  }
  retained.current.set(signature, retainedAttempt);
  return retainedAttempt;
};

const releaseDefinitiveAttempt = (
  retained: React.MutableRefObject<Map<string, RetainedAutomationAttempt>>,
  signature: string,
  error?: unknown,
): void => {
  if (error === undefined || !isRetryableStreamRequestError(error)) {
    const attempt = retained.current.get(signature);
    if (attempt) {
      clearPendingIdempotencyAttempt({
        namespace: attempt.namespace,
        operation: attempt.operation,
        requestFingerprint: attempt.requestFingerprint,
        expectedIdempotencyKey: attempt.idempotencyKey,
        storage: getAutomationAttemptStorage(),
      });
    }
    retained.current.delete(signature);
  }
};

export const useAutomationWorkspace = (
  selectedIndexId: string | null,
  persistence: AutomationWorkspacePersistenceOptions = {},
): AutomationWorkspaceResult => {
  const { currentUser } = useAuth();
  const scopeId = currentUser?.uid || '__anonymous__';
  const attemptNamespace = `automations:${scopeId}`;
  const enabled = persistence.enabled !== false;
  const loadImportReferences = enabled && persistence.loadImportReferences !== false;
  const loadConnectorReferences = enabled && persistence.loadConnectorReferences !== false;
  const engineCatalog = useEngineCatalog({ autoLoad: loadImportReferences });
  const refreshEngineCatalog = engineCatalog.refresh;
  const onDirtyDraftsChange = persistence.onDirtyDraftsChange;

  const [indexes, setIndexes] = useState<Index[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [referenceErrors, setReferenceErrors] = useState<ReferenceErrors>({
    indexes: null,
    connectors: null,
  });
  const [isLoadingIndexes, setIsLoadingIndexes] = useState(true);
  const [isLoadingConnectors, setIsLoadingConnectors] = useState(true);

  const [savedImportDraft, setSavedImportDraft] = useState<ImportAutomationDraft | null>(null);
  const [savedExportDraft, setSavedExportDraft] = useState<ExportAutomationDraft | null>(null);
  const isPageVisible = usePassiveApiActivity(
    savedImportDraft?.runtime_status === 'provisioning' || savedExportDraft?.runtime_status === 'provisioning',
    enabled && persistence.isActive !== false,
  );
  const [draftScopeIndexId, setDraftScopeIndexId] = useState<string | null>(null);
  const draftScopeIndexIdRef = useRef<string | null>(null);
  const [importDraftState, setImportDraftState] = useState<ImportAutomationDraft>(
    createDefaultImportAutomationDraft,
  );
  const [exportDraftState, setExportDraftState] = useState<ExportAutomationDraft>(
    createDefaultExportAutomationDraft,
  );
  const [isLoadingImport, setIsLoadingImport] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [isSavingImport, setIsSavingImport] = useState(false);
  const [isSavingExport, setIsSavingExport] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [importMessage, setImportMessage] = useState<AutomationMessage | null>(null);
  const [exportMessage, setExportMessage] = useState<AutomationMessage | null>(null);
  const [importReconcileRevision, setImportReconcileRevision] = useState(0);
  const [exportReconcileRevision, setExportReconcileRevision] = useState(0);

  const dirtyDraftsByIndexRef = useRef<AutomationDirtyDraftsByIndex>({
    ...(persistence.initialDirtyDraftsByIndex || {}),
  });
  const baseReferenceGenerationRef = useRef(0);
  const connectorReferenceGenerationRef = useRef(0);
  const importLoadGenerationRef = useRef(0);
  const exportLoadGenerationRef = useRef(0);
  const importMutationGenerationRef = useRef(0);
  const exportMutationGenerationRef = useRef(0);
  const isSavingImportRef = useRef(false);
  const isSavingExportRef = useRef(false);
  const selectedIndexRef = useRef<string | null>(null);
  const importDirtyRef = useRef(false);
  const exportDirtyRef = useRef(false);
  const importAttemptRef = useRef<Map<string, RetainedAutomationAttempt>>(new Map());
  const exportAttemptRef = useRef<Map<string, RetainedAutomationAttempt>>(new Map());
  const importPauseAttemptRef = useRef<Map<string, RetainedAutomationAttempt>>(new Map());
  const exportPauseAttemptRef = useRef<Map<string, RetainedAutomationAttempt>>(new Map());
  const pendingImportReconciliationRef = useRef<Map<string, PendingMutationReconciliation>>(
    new Map(),
  );
  const pendingExportReconciliationRef = useRef<Map<string, PendingMutationReconciliation>>(
    new Map(),
  );
  const importBackgroundLoadInFlightRef = useRef(false);
  const exportBackgroundLoadInFlightRef = useRef(false);
  const importWasPageVisibleRef = useRef(isPageVisible);
  const exportWasPageVisibleRef = useRef(isPageVisible);
  const provisioningAttemptsRef = useRef<Record<AutomationKind, Map<string, number>>>(
    { import: new Map(), export: new Map() },
  );
  const importDraftStateRef = useRef(importDraftState);
  const exportDraftStateRef = useRef(exportDraftState);
  const importDraftRevisionRef = useRef(0);
  const exportDraftRevisionRef = useRef(0);
  const savedImportDraftRef = useRef(savedImportDraft);
  const savedExportDraftRef = useRef(savedExportDraft);
  const importBaselineLoadedRef = useRef<Set<string>>(new Set());
  const exportBaselineLoadedRef = useRef<Set<string>>(new Set());

  importDraftStateRef.current = importDraftState;
  exportDraftStateRef.current = exportDraftState;
  savedImportDraftRef.current = savedImportDraft;
  savedExportDraftRef.current = savedExportDraft;

  useEffect(() => {
    dirtyDraftsByIndexRef.current = {
      ...(persistence.initialDirtyDraftsByIndex || {}),
    };
  }, [persistence.initialDirtyDraftsByIndex]);

  const commitDirtyDrafts = useCallback((
    indexId: string,
    patch: Partial<AutomationIndexDirtyDrafts>,
  ): void => {
    const normalizedIndexId = indexId.trim();
    if (!normalizedIndexId) return;
    const current = dirtyDraftsByIndexRef.current[normalizedIndexId] || {
      importDraft: null,
      exportDraft: null,
    };
    const next: AutomationIndexDirtyDrafts = { ...current, ...patch };
    if (!next.importDraft && !next.exportDraft) {
      delete dirtyDraftsByIndexRef.current[normalizedIndexId];
      onDirtyDraftsChange?.(normalizedIndexId, null);
      return;
    }
    dirtyDraftsByIndexRef.current[normalizedIndexId] = next;
    onDirtyDraftsChange?.(normalizedIndexId, next);
  }, [onDirtyDraftsChange]);

  const eligibleIndexes = useMemo(
    () => sortIndexes(
      indexes.filter((index) => !index.is_default && index.index_id !== 'playground'),
      'name',
      true,
    ),
    [indexes],
  );
  const selectedIndex = useMemo(
    () => enabled
      ? eligibleIndexes.find((index) => index.index_id === selectedIndexId) || null
      : null,
    [eligibleIndexes, enabled, selectedIndexId],
  );
  const selectedIndexKey = selectedIndex?.index_id || null;
  const pendingImportMutation = selectedIndexId
    ? pendingImportReconciliationRef.current.get(selectedIndexId)?.attemptChannel || null
    : null;
  const pendingExportMutation = selectedIndexId
    ? pendingExportReconciliationRef.current.get(selectedIndexId)?.attemptChannel || null
    : null;
  // Draft ownership follows the user's selected identity, not a transient
  // catalog snapshot. If an index disappears during refresh, keep its edits
  // visible until the user explicitly discards them or chooses another index.
  const draftScopeMatchesSelection = draftScopeIndexId === selectedIndexId;
  selectedIndexRef.current = selectedIndex?.index_id || null;

  const importConnectors = useMemo(
    () => connectors.filter(isImportAutomationConnector),
    [connectors],
  );
  const exportConnectors = useMemo(
    () => connectors.filter(isExportAutomationConnector),
    [connectors],
  );

  const getImportValidationError = useCallback((draft: ImportAutomationDraft): string | null => {
    if (
      isLoadingIndexes
      || isLoadingConnectors
      || engineCatalog.isLoading
      || referenceErrors.indexes
      || referenceErrors.connectors
      || engineCatalog.error
    ) {
      return 'Refresh indexes, connectors, and engines before saving or resuming.';
    }
    if (!draft.connector_id) return 'Select an import connector.';
    if (!importConnectors.some((connector) => connector.connector_id === draft.connector_id)) {
      return 'Choose an available import connector.';
    }
    if (!draft.prompt_id) return 'Select an extraction engine.';
    if (!engineCatalog.prompts.some((prompt) => prompt.prompt_id === draft.prompt_id)) {
      return 'Choose an available extraction engine.';
    }
    if (!isAutomationDebounceInterval(draft.debounce_interval)) {
      return 'Choose an available check interval.';
    }
    return getImportAutomationDraftContractError(draft);
  }, [
    engineCatalog.error,
    engineCatalog.isLoading,
    engineCatalog.prompts,
    importConnectors,
    isLoadingConnectors,
    isLoadingIndexes,
    referenceErrors,
  ]);

  const getExportValidationError = useCallback((draft: ExportAutomationDraft): string | null => {
    if (
      isLoadingIndexes
      || isLoadingConnectors
      || referenceErrors.indexes
      || referenceErrors.connectors
    ) {
      return 'Refresh indexes and connectors before saving or resuming.';
    }
    if (!draft.destination_connector_id) return 'Select an export connector.';
    if (!exportConnectors.some(
      (connector) => connector.connector_id === draft.destination_connector_id,
    )) {
      return 'Choose an available export connector.';
    }
    return normalizeExportDestinationSubpath(draft.destination_subpath).error;
  }, [
    exportConnectors,
    isLoadingConnectors,
    isLoadingIndexes,
    referenceErrors.connectors,
    referenceErrors.indexes,
  ]);

  const importDirty = useMemo(
    () => Boolean(
      draftScopeIndexId
      && dirtyDraftsByIndexRef.current[draftScopeIndexId]?.importDraft
    ) || editableImportSignature(importDraftState) !== editableImportSignature(
      savedImportDraft || createDefaultImportAutomationDraft(),
    ),
    [draftScopeIndexId, importDraftState, savedImportDraft],
  );
  const exportDirty = useMemo(
    () => Boolean(
      draftScopeIndexId
      && dirtyDraftsByIndexRef.current[draftScopeIndexId]?.exportDraft
    ) || editableExportSignature(exportDraftState) !== editableExportSignature(
      savedExportDraft || createDefaultExportAutomationDraft(),
    ),
    [draftScopeIndexId, exportDraftState, savedExportDraft],
  );
  importDirtyRef.current = importDirty;
  exportDirtyRef.current = exportDirty;

  const setImportDraft = useCallback<React.Dispatch<React.SetStateAction<ImportAutomationDraft>>>(
    (next) => {
      setImportMessage(null);
      const current = importDraftStateRef.current;
      const nextDraft = typeof next === 'function'
        ? (next as (previous: ImportAutomationDraft) => ImportAutomationDraft)(current)
        : next;
      importDraftRevisionRef.current += 1;
      importDraftStateRef.current = nextDraft;
      setImportDraftState(nextDraft);
      const indexId = draftScopeIndexIdRef.current;
      if (!indexId) return;
      const baseline = savedImportDraftRef.current || createDefaultImportAutomationDraft();
      const isDirty = !importBaselineLoadedRef.current.has(indexId)
        || editableImportSignature(nextDraft) !== editableImportSignature(baseline);
      commitDirtyDrafts(indexId, {
        importDraft: isDirty ? persistableImportDraft(nextDraft) : null,
      });
    },
    [commitDirtyDrafts],
  );
  const setExportDraft = useCallback<React.Dispatch<React.SetStateAction<ExportAutomationDraft>>>(
    (next) => {
      setExportMessage(null);
      const current = exportDraftStateRef.current;
      const nextDraft = typeof next === 'function'
        ? (next as (previous: ExportAutomationDraft) => ExportAutomationDraft)(current)
        : next;
      exportDraftRevisionRef.current += 1;
      exportDraftStateRef.current = nextDraft;
      setExportDraftState(nextDraft);
      const indexId = draftScopeIndexIdRef.current;
      if (!indexId) return;
      const baseline = savedExportDraftRef.current || createDefaultExportAutomationDraft();
      const isDirty = !exportBaselineLoadedRef.current.has(indexId)
        || editableExportSignature(nextDraft) !== editableExportSignature(baseline);
      commitDirtyDrafts(indexId, {
        exportDraft: isDirty ? persistableExportDraft(nextDraft) : null,
      });
    },
    [commitDirtyDrafts],
  );

  const refreshBaseReferences = useCallback(async (): Promise<void> => {
    const generation = ++baseReferenceGenerationRef.current;
    const requestScope = scopeId;
    setIsLoadingIndexes(true);
    setReferenceErrors((current) => ({
      ...current,
      indexes: null,
    }));

    const requestIsCurrent = () => (
      generation === baseReferenceGenerationRef.current && requestScope === scopeId
    );
    await apiService.getIndexes({ includePreview: true })
      .then((value) => {
        if (!requestIsCurrent()) return;
        setIndexes(Array.isArray(value) ? value : []);
      })
      .catch((error) => {
        if (!requestIsCurrent()) return;
        setReferenceErrors((current) => ({
          ...current,
          indexes: getApiErrorMessage(error, 'Failed to load indexes.'),
        }));
      })
      .finally(() => {
        if (requestIsCurrent()) setIsLoadingIndexes(false);
      });
  }, [scopeId]);

  const refreshConnectors = useCallback(async (): Promise<void> => {
    const generation = ++connectorReferenceGenerationRef.current;
    setIsLoadingConnectors(true);
    setReferenceErrors((current) => ({ ...current, connectors: null }));
    const requestIsCurrent = () => generation === connectorReferenceGenerationRef.current;
    await apiService.getConnectors()
      .then((value) => {
        if (!requestIsCurrent()) return;
        setConnectors(Array.isArray(value) ? value : []);
      })
      .catch((error) => {
        if (!requestIsCurrent()) return;
        setReferenceErrors((current) => ({
          ...current,
          connectors: getApiErrorMessage(error, 'Failed to load cloud connectors.'),
        }));
      })
      .finally(() => {
        if (requestIsCurrent()) setIsLoadingConnectors(false);
      });
  }, []);

  useEffect(() => {
    if (!loadConnectorReferences) return;
    void refreshConnectors();
    return () => { connectorReferenceGenerationRef.current += 1; };
  }, [loadConnectorReferences, refreshConnectors, scopeId]);

  useEffect(() => {
    importAttemptRef.current.clear();
    exportAttemptRef.current.clear();
    importPauseAttemptRef.current.clear();
    exportPauseAttemptRef.current.clear();
    pendingImportReconciliationRef.current.clear();
    pendingExportReconciliationRef.current.clear();
    provisioningAttemptsRef.current.import.clear();
    provisioningAttemptsRef.current.export.clear();
  }, [scopeId]);

  useEffect(() => {
    if (!enabled) return;
    baseReferenceGenerationRef.current += 1;
    setIndexes([]);
    setConnectors([]);
    setIsLoadingIndexes(true);
    setIsLoadingConnectors(true);
    setReferenceErrors((current) => ({
      ...current,
      indexes: null,
      connectors: null,
    }));
    void refreshBaseReferences();
    return () => {
      baseReferenceGenerationRef.current += 1;
    };
  }, [enabled, refreshBaseReferences]);

  const loadImport = useCallback(async (
    indexId: string,
    options: AutomationLoadOptions = {},
  ): Promise<void> => {
    if (options.background && importBackgroundLoadInFlightRef.current) return;
    if (options.background) importBackgroundLoadInFlightRef.current = true;
    const generation = ++importLoadGenerationRef.current;
    if (!options.background) setIsLoadingImport(true);
    setImportError(null);
    try {
      const config = await apiService.getImportAutomation(indexId);
      if (
        generation !== importLoadGenerationRef.current
        || selectedIndexRef.current !== indexId
      ) return;
      const nextSaved = config ? toImportAutomationDraft(config) : null;
      savedImportDraftRef.current = nextSaved;
      setSavedImportDraft(nextSaved);
      const pendingReconciliation = pendingImportReconciliationRef.current.get(indexId) || null;
      const pendingMutationObserved = Boolean(
        nextSaved
        && pendingReconciliation
        && editableImportSignature(nextSaved) === pendingReconciliation.expectedSignature
      );
      const pendingTargetGone = Boolean(
        !nextSaved
        && pendingReconciliation
        && pendingReconciliation.baselineSignature !== importAutomationSnapshotSignature(null)
      );
      const mutationStateChanged = Boolean(
        options.mutationErrorObservation
        && importAutomationSnapshotSignature(nextSaved)
          !== options.mutationErrorObservation.baselineSignature
      );
      const definitiveMutationObserved = Boolean(
        nextSaved
        && options.mutationErrorObservation
        && editableImportSignature(nextSaved) === options.mutationErrorObservation.expectedSignature
      );
      if (pendingTargetGone && pendingReconciliation) {
        pendingImportReconciliationRef.current.delete(indexId);
        provisioningAttemptsRef.current.import.delete(indexId);
        releaseDefinitiveAttempt(
          pendingReconciliation.attemptChannel === 'save'
            ? importAttemptRef
            : importPauseAttemptRef,
          pendingReconciliation.attemptSignature,
        );
        setImportMessage({
          type: 'error',
          text: 'Import automation no longer exists. Refresh before retrying.',
        });
      } else if (pendingMutationObserved && nextSaved) {
        if (nextSaved.runtime_status !== 'provisioning') {
          pendingImportReconciliationRef.current.delete(indexId);
          provisioningAttemptsRef.current.import.delete(indexId);
          releaseDefinitiveAttempt(
            pendingReconciliation!.attemptChannel === 'save'
              ? importAttemptRef
              : importPauseAttemptRef,
            pendingReconciliation!.attemptSignature,
          );
        }
        setImportMessage(getAutomationMutationMessage(
          nextSaved,
          pendingReconciliation!.successMessage,
          pendingReconciliation!.failureMessage,
        ));
      } else if (options.mutationErrorObservation) {
        if (mutationStateChanged && definitiveMutationObserved && nextSaved) {
          setImportMessage(getAutomationMutationMessage(
            nextSaved,
            options.mutationErrorObservation.successMessage,
            options.mutationErrorObservation.failureMessage,
          ));
        }
      } else {
        setImportMessage((current) => (
          (nextSaved?.runtime_status === 'provisioning' || pendingReconciliation)
          && current?.type === 'info'
            ? current
            : null
        ));
      }
      const persistedDraft = dirtyDraftsByIndexRef.current[indexId]?.importDraft || null;
      const shouldPreserveDraft = Boolean(
        options.preserveDraft
        || (options.preserveDirty && (importDirtyRef.current || persistedDraft)),
      );
      const currentDraft = importDraftStateRef.current;
      const nextDraft = shouldPreserveDraft
        ? {
            ...currentDraft,
            runtime_status: nextSaved?.runtime_status ?? null,
            runtime_error: nextSaved?.runtime_error ?? null,
          }
        : nextSaved || createDefaultImportAutomationDraft();
      importBaselineLoadedRef.current.add(indexId);
      importDraftStateRef.current = nextDraft;
      setImportDraftState(nextDraft);
      const baseline = nextSaved || createDefaultImportAutomationDraft();
      const editableMatchesBaseline = (
        editableImportSignature(nextDraft) === editableImportSignature(baseline)
      );
      const isTerminal = nextSaved?.runtime_status !== 'provisioning';
      commitDirtyDrafts(indexId, {
        importDraft: !editableMatchesBaseline || (Boolean(persistedDraft) && !isTerminal)
          ? persistableImportDraft(nextDraft)
          : null,
      });
      if (persistedDraft && editableMatchesBaseline && isTerminal) {
        try {
          await clearDurableAutomationAttempt(
            attemptNamespace,
            'import-save',
            { index_id: indexId, payload: buildImportMutationPayload(nextDraft) },
          );
        } catch {
          // A failed cleanup is safe: the durable owner continues to protect an
          // identical retry instead of allowing a second idempotency identity.
        }
      }
      if (nextSaved?.enabled && isTerminal) {
        const action = isAutomationPaused(nextSaved) ? 'pause' : 'resume';
        try {
          await clearDurableAutomationAttempt(
            attemptNamespace,
            'import-status',
            { index_id: indexId, action },
          );
        } catch {
          // Retain uncertain ownership on storage/read failure.
        }
      }
    } catch (error) {
      if (
        generation !== importLoadGenerationRef.current
        || selectedIndexRef.current !== indexId
      ) return;
      setImportError(getApiErrorMessage(error, 'Failed to load import automation settings.'));
      if (options.background && !pendingImportReconciliationRef.current.has(indexId)) {
        setImportMessage(null);
      }
    } finally {
      if (options.background) importBackgroundLoadInFlightRef.current = false;
      if (
        generation === importLoadGenerationRef.current
        && selectedIndexRef.current === indexId
        && !options.background
      ) {
        setIsLoadingImport(false);
      }
    }
  }, [attemptNamespace, commitDirtyDrafts]);

  const loadExport = useCallback(async (
    indexId: string,
    options: AutomationLoadOptions = {},
  ): Promise<void> => {
    if (options.background && exportBackgroundLoadInFlightRef.current) return;
    if (options.background) exportBackgroundLoadInFlightRef.current = true;
    const generation = ++exportLoadGenerationRef.current;
    if (!options.background) setIsLoadingExport(true);
    setExportError(null);
    try {
      const config = await apiService.getExportAutomation(indexId);
      if (
        generation !== exportLoadGenerationRef.current
        || selectedIndexRef.current !== indexId
      ) return;
      const nextSaved = config ? toExportAutomationDraft(config) : null;
      savedExportDraftRef.current = nextSaved;
      setSavedExportDraft(nextSaved);
      const pendingReconciliation = pendingExportReconciliationRef.current.get(indexId) || null;
      const pendingMutationObserved = Boolean(
        nextSaved
        && pendingReconciliation
        && editableExportSignature(nextSaved) === pendingReconciliation.expectedSignature
      );
      const pendingTargetGone = Boolean(
        !nextSaved
        && pendingReconciliation
        && pendingReconciliation.baselineSignature !== exportAutomationSnapshotSignature(null)
      );
      const mutationStateChanged = Boolean(
        options.mutationErrorObservation
        && exportAutomationSnapshotSignature(nextSaved)
          !== options.mutationErrorObservation.baselineSignature
      );
      const definitiveMutationObserved = Boolean(
        nextSaved
        && options.mutationErrorObservation
        && editableExportSignature(nextSaved) === options.mutationErrorObservation.expectedSignature
      );
      if (pendingTargetGone && pendingReconciliation) {
        pendingExportReconciliationRef.current.delete(indexId);
        provisioningAttemptsRef.current.export.delete(indexId);
        releaseDefinitiveAttempt(
          pendingReconciliation.attemptChannel === 'save'
            ? exportAttemptRef
            : exportPauseAttemptRef,
          pendingReconciliation.attemptSignature,
        );
        setExportMessage({
          type: 'error',
          text: 'Export automation no longer exists. Refresh before retrying.',
        });
      } else if (pendingMutationObserved && nextSaved) {
        if (nextSaved.runtime_status !== 'provisioning') {
          pendingExportReconciliationRef.current.delete(indexId);
          provisioningAttemptsRef.current.export.delete(indexId);
          releaseDefinitiveAttempt(
            pendingReconciliation!.attemptChannel === 'save'
              ? exportAttemptRef
              : exportPauseAttemptRef,
            pendingReconciliation!.attemptSignature,
          );
        }
        setExportMessage(getAutomationMutationMessage(
          nextSaved,
          pendingReconciliation!.successMessage,
          pendingReconciliation!.failureMessage,
        ));
      } else if (options.mutationErrorObservation) {
        if (mutationStateChanged && definitiveMutationObserved && nextSaved) {
          setExportMessage(getAutomationMutationMessage(
            nextSaved,
            options.mutationErrorObservation.successMessage,
            options.mutationErrorObservation.failureMessage,
          ));
        }
      } else {
        setExportMessage((current) => (
          (nextSaved?.runtime_status === 'provisioning' || pendingReconciliation)
          && current?.type === 'info'
            ? current
            : null
        ));
      }
      const persistedDraft = dirtyDraftsByIndexRef.current[indexId]?.exportDraft || null;
      const shouldPreserveDraft = Boolean(
        options.preserveDraft
        || (options.preserveDirty && (exportDirtyRef.current || persistedDraft)),
      );
      const currentDraft = exportDraftStateRef.current;
      const nextDraft = shouldPreserveDraft
        ? {
            ...currentDraft,
            runtime_status: nextSaved?.runtime_status ?? null,
            runtime_error: nextSaved?.runtime_error ?? null,
          }
        : nextSaved || createDefaultExportAutomationDraft();
      exportBaselineLoadedRef.current.add(indexId);
      exportDraftStateRef.current = nextDraft;
      setExportDraftState(nextDraft);
      const baseline = nextSaved || createDefaultExportAutomationDraft();
      const editableMatchesBaseline = (
        editableExportSignature(nextDraft) === editableExportSignature(baseline)
      );
      const isTerminal = nextSaved?.runtime_status !== 'provisioning';
      commitDirtyDrafts(indexId, {
        exportDraft: !editableMatchesBaseline || (Boolean(persistedDraft) && !isTerminal)
          ? persistableExportDraft(nextDraft)
          : null,
      });
      if (persistedDraft && editableMatchesBaseline && isTerminal) {
        try {
          await clearDurableAutomationAttempt(
            attemptNamespace,
            'export-save',
            { index_id: indexId, payload: buildExportMutationPayload(nextDraft) },
          );
        } catch {
          // Keep the durable owner when cleanup cannot be established safely.
        }
      }
      if (nextSaved?.enabled && isTerminal) {
        const action = isAutomationPaused(nextSaved) ? 'pause' : 'resume';
        try {
          await clearDurableAutomationAttempt(
            attemptNamespace,
            'export-status',
            { index_id: indexId, action },
          );
        } catch {
          // Retain uncertain ownership on storage/read failure.
        }
      }
    } catch (error) {
      if (
        generation !== exportLoadGenerationRef.current
        || selectedIndexRef.current !== indexId
      ) return;
      setExportError(getApiErrorMessage(error, 'Failed to load export automation settings.'));
      if (options.background && !pendingExportReconciliationRef.current.has(indexId)) {
        setExportMessage(null);
      }
    } finally {
      if (options.background) exportBackgroundLoadInFlightRef.current = false;
      if (
        generation === exportLoadGenerationRef.current
        && selectedIndexRef.current === indexId
        && !options.background
      ) {
        setIsLoadingExport(false);
      }
    }
  }, [attemptNamespace, commitDirtyDrafts]);

  const reconcileAmbiguousImportMutation = useCallback(async (
    indexId: string,
    reconciliation: PendingMutationReconciliation,
  ): Promise<void> => {
    pendingImportReconciliationRef.current.set(indexId, reconciliation);
    provisioningAttemptsRef.current.import.set(indexId, 0);
    setImportMessage({
      type: 'info',
      text: 'The update response was interrupted. Confirming the import automation state…',
    });
    setImportReconcileRevision((revision) => revision + 1);
    await loadImport(indexId, { preserveDirty: true }).finally(() => {
      if (selectedIndexRef.current === indexId) {
        setImportReconcileRevision((revision) => revision + 1);
      }
    });
  }, [loadImport]);

  const reconcileAmbiguousExportMutation = useCallback(async (
    indexId: string,
    reconciliation: PendingMutationReconciliation,
  ): Promise<void> => {
    pendingExportReconciliationRef.current.set(indexId, reconciliation);
    provisioningAttemptsRef.current.export.set(indexId, 0);
    setExportMessage({
      type: 'info',
      text: 'The update response was interrupted. Confirming the export automation state…',
    });
    setExportReconcileRevision((revision) => revision + 1);
    await loadExport(indexId, { preserveDirty: true }).finally(() => {
      if (selectedIndexRef.current === indexId) {
        setExportReconcileRevision((revision) => revision + 1);
      }
    });
  }, [loadExport]);

  const observeDefinitiveImportMutationError = useCallback(async (
    indexId: string,
    observation: MutationErrorObservation,
  ): Promise<void> => {
    // A definitive transport response releases the idempotency attempt and must
    // not inherit a prior ambiguous-write poll. The backend can still persist a
    // terminal scheduler state before returning a structured application error,
    // so take one authoritative snapshot and surface it only if it changed.
    pendingImportReconciliationRef.current.delete(indexId);
    await loadImport(indexId, {
      preserveDirty: true,
      mutationErrorObservation: observation,
    });
  }, [loadImport]);

  const observeDefinitiveExportMutationError = useCallback(async (
    indexId: string,
    observation: MutationErrorObservation,
  ): Promise<void> => {
    pendingExportReconciliationRef.current.delete(indexId);
    await loadExport(indexId, {
      preserveDirty: true,
      mutationErrorObservation: observation,
    });
  }, [loadExport]);

  useEffect(() => {
    importLoadGenerationRef.current += 1;
    exportLoadGenerationRef.current += 1;
    importMutationGenerationRef.current += 1;
    exportMutationGenerationRef.current += 1;
    isSavingImportRef.current = false;
    isSavingExportRef.current = false;
    setIsSavingImport(false);
    setIsSavingExport(false);
    setIsLoadingImport(false);
    setIsLoadingExport(false);
    const indexId = selectedIndex?.index_id;
    const hasDirtyDraftForUnavailableSelection = Boolean(
      selectedIndexId
      && !indexId
      && draftScopeIndexIdRef.current === selectedIndexId
      && (
        importDirtyRef.current
        || exportDirtyRef.current
        || dirtyDraftsByIndexRef.current[selectedIndexId]?.importDraft
        || dirtyDraftsByIndexRef.current[selectedIndexId]?.exportDraft
      )
    );
    if (hasDirtyDraftForUnavailableSelection) {
      // Fence old requests, but do not erase the only copy of the user's
      // edits merely because a refreshed catalog temporarily omitted the ID.
      setIsLoadingImport(false);
      setIsLoadingExport(false);
      return () => {
        importLoadGenerationRef.current += 1;
        exportLoadGenerationRef.current += 1;
        importMutationGenerationRef.current += 1;
        exportMutationGenerationRef.current += 1;
      };
    }

    const preserveCurrentDirtyDraft = Boolean(
      indexId
      && draftScopeIndexIdRef.current === indexId
      && (importDirtyRef.current || exportDirtyRef.current)
    );
    setImportMessage(null);
    setExportMessage(null);
    setImportError(null);
    setExportError(null);
    const restoredDrafts = indexId
      ? dirtyDraftsByIndexRef.current[indexId] || null
      : null;
    if (!preserveCurrentDirtyDraft) {
      const nextImportDraft = restoredDrafts?.importDraft
        ? { ...restoredDrafts.importDraft }
        : createDefaultImportAutomationDraft();
      const nextExportDraft = restoredDrafts?.exportDraft
        ? { ...restoredDrafts.exportDraft }
        : createDefaultExportAutomationDraft();
      savedImportDraftRef.current = null;
      savedExportDraftRef.current = null;
      importDraftStateRef.current = nextImportDraft;
      exportDraftStateRef.current = nextExportDraft;
      setSavedImportDraft(null);
      setSavedExportDraft(null);
      setImportDraftState(nextImportDraft);
      setExportDraftState(nextExportDraft);
    }
    setImportReconcileRevision(0);
    setExportReconcileRevision(0);

    draftScopeIndexIdRef.current = indexId || null;
    setDraftScopeIndexId(indexId || null);
    if (!indexId) {
      setIsLoadingImport(false);
      setIsLoadingExport(false);
      return;
    }
    importBaselineLoadedRef.current.delete(indexId);
    exportBaselineLoadedRef.current.delete(indexId);
    importDirtyRef.current = Boolean(
      preserveCurrentDirtyDraft || restoredDrafts?.importDraft,
    );
    exportDirtyRef.current = Boolean(
      preserveCurrentDirtyDraft || restoredDrafts?.exportDraft,
    );
    void loadExport(indexId, {
      preserveDirty: preserveCurrentDirtyDraft,
      preserveDraft: Boolean(restoredDrafts?.exportDraft),
    });
    return () => {
      importLoadGenerationRef.current += 1;
      exportLoadGenerationRef.current += 1;
      importMutationGenerationRef.current += 1;
      exportMutationGenerationRef.current += 1;
    };
  }, [loadExport, selectedIndex?.index_id, selectedIndexId]);

  useEffect(() => {
    const indexId = selectedIndex?.index_id;
    if (
      !indexId
      || draftScopeIndexIdRef.current !== indexId
      || importBaselineLoadedRef.current.has(indexId)
    ) return;
    const restoredDraft = dirtyDraftsByIndexRef.current[indexId]?.importDraft || null;
    void loadImport(indexId, {
      preserveDirty: importDirtyRef.current,
      preserveDraft: Boolean(restoredDraft),
    });
  }, [loadImport, selectedIndex?.index_id]);

  const retryImport = useCallback(async (): Promise<void> => {
    const indexId = selectedIndexRef.current;
    if (indexId) await loadImport(indexId, { preserveDirty: true });
  }, [loadImport]);

  const retryExport = useCallback(async (): Promise<void> => {
    const indexId = selectedIndexRef.current;
    if (indexId) await loadExport(indexId, { preserveDirty: true });
  }, [loadExport]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    const indexId = selectedIndexRef.current;
    await Promise.all([
      refreshBaseReferences(),
      ...(loadConnectorReferences ? [refreshConnectors()] : []),
      ...(loadImportReferences ? [refreshEngineCatalog()] : []),
      ...(indexId
        ? [
            loadExport(indexId, { preserveDirty: true }),
            loadImport(indexId, { preserveDirty: true }),
          ]
        : []),
    ]);
  }, [
    enabled,
    loadExport,
    loadImport,
    loadImportReferences,
    loadConnectorReferences,
    refreshBaseReferences,
    refreshConnectors,
    refreshEngineCatalog,
  ]);

  useEffect(() => {
    const indexId = selectedIndex?.index_id;
    const becameVisible = isPageVisible && !importWasPageVisibleRef.current;
    importWasPageVisibleRef.current = isPageVisible;
    const shouldReconcile = (
      savedImportDraft?.runtime_status === 'provisioning'
      || Boolean(indexId && pendingImportReconciliationRef.current.has(indexId))
    );
    if (
      !indexId
      || !isPageVisible
      || !shouldReconcile
      || isLoadingImport
      || isSavingImport
    ) {
      if (!shouldReconcile) {
        if (indexId) provisioningAttemptsRef.current.import.delete(indexId);
      }
      return;
    }
    if (becameVisible) {
      void loadImport(indexId, { preserveDirty: true, background: true }).finally(() => {
        if (selectedIndexRef.current === indexId) {
          setImportReconcileRevision((revision) => revision + 1);
        }
      });
      return;
    }
    const attempt = provisioningAttemptsRef.current.import.get(indexId) || 0;
    const delay = getProvisioningReconcileDelay(attempt);
    if (delay === null) {
      const text = 'Import provisioning is still pending. Refresh to check its latest state.';
      setImportMessage((current) => (
        current?.type === 'info' && current.text === text
          ? current
          : { type: 'info', text }
      ));
      return;
    }
    const timeoutId = window.setTimeout(() => {
      provisioningAttemptsRef.current.import.set(indexId, attempt + 1);
      void loadImport(indexId, { preserveDirty: true, background: true }).finally(() => {
        if (selectedIndexRef.current === indexId) {
          setImportReconcileRevision((revision) => revision + 1);
        }
      });
    }, delay);
    return () => window.clearTimeout(timeoutId);
  }, [
    importReconcileRevision,
    isPageVisible,
    isLoadingImport,
    isSavingImport,
    loadImport,
    savedImportDraft,
    selectedIndex?.index_id,
  ]);

  useEffect(() => {
    const indexId = selectedIndex?.index_id;
    const becameVisible = isPageVisible && !exportWasPageVisibleRef.current;
    exportWasPageVisibleRef.current = isPageVisible;
    const shouldReconcile = (
      savedExportDraft?.runtime_status === 'provisioning'
      || Boolean(indexId && pendingExportReconciliationRef.current.has(indexId))
    );
    if (
      !indexId
      || !isPageVisible
      || !shouldReconcile
      || isLoadingExport
      || isSavingExport
    ) {
      if (!shouldReconcile) {
        if (indexId) provisioningAttemptsRef.current.export.delete(indexId);
      }
      return;
    }
    if (becameVisible) {
      void loadExport(indexId, { preserveDirty: true, background: true }).finally(() => {
        if (selectedIndexRef.current === indexId) {
          setExportReconcileRevision((revision) => revision + 1);
        }
      });
      return;
    }
    const attempt = provisioningAttemptsRef.current.export.get(indexId) || 0;
    const delay = getProvisioningReconcileDelay(attempt);
    if (delay === null) {
      const text = 'Export provisioning is still pending. Refresh to check its latest state.';
      setExportMessage((current) => (
        current?.type === 'info' && current.text === text
          ? current
          : { type: 'info', text }
      ));
      return;
    }
    const timeoutId = window.setTimeout(() => {
      provisioningAttemptsRef.current.export.set(indexId, attempt + 1);
      void loadExport(indexId, { preserveDirty: true, background: true }).finally(() => {
        if (selectedIndexRef.current === indexId) {
          setExportReconcileRevision((revision) => revision + 1);
        }
      });
    }, delay);
    return () => window.clearTimeout(timeoutId);
  }, [
    exportReconcileRevision,
    isPageVisible,
    isLoadingExport,
    isSavingExport,
    loadExport,
    savedExportDraft,
    selectedIndex?.index_id,
  ]);

  const saveImport = useCallback(async (): Promise<boolean> => {
    const indexId = selectedIndexRef.current;
    const draft = importDraftStateRef.current;
    const savedDraft = savedImportDraftRef.current;
    if (
      !indexId
      || draftScopeIndexIdRef.current !== indexId
      || isSavingImportRef.current
    ) return false;
    if (savedDraft?.runtime_status === 'provisioning') {
      setImportMessage({
        type: 'info',
        text: 'Wait for the current scheduler update to finish before saving again.',
      });
      return false;
    }
    const pendingReconciliation = pendingImportReconciliationRef.current.get(indexId);
    if (pendingReconciliation && pendingReconciliation.attemptChannel !== 'save') {
      setImportMessage({
        type: 'info',
        text: 'Wait for the pending import update to finish before starting another change.',
      });
      return false;
    }
    const validationError = getImportValidationError(draft);
    if (validationError) {
      setImportMessage({ type: 'error', text: validationError });
      return false;
    }
    const baselineSignature = importAutomationSnapshotSignature(savedDraft);
    const successMessage = 'Import automation saved.';
    const failureMessage = 'Scheduler provisioning failed. Import automation is not active.';

    const payload = buildImportMutationPayload(draft);
    const expectedReconcileSignature = editableImportSignature({
      ...draft,
      paused: payload.paused,
      file_pattern: payload.file_pattern,
    });
    const signature = JSON.stringify([indexId, 'save', payload]);
    const draftSignature = editableImportSignature(draft);
    const draftRevision = importDraftRevisionRef.current;
    if (pendingReconciliation && pendingReconciliation.attemptSignature !== signature) {
      setImportMessage({
        type: 'info',
        text: 'Wait for the pending import update to finish before starting another change.',
      });
      return false;
    }
    // Own this mutation channel before durable fingerprinting touches storage.
    // This makes a rapid second click, edit, or route change observable before
    // any network request can be dispatched.
    importLoadGenerationRef.current += 1;
    const generation = ++importMutationGenerationRef.current;
    isSavingImportRef.current = true;
    setIsSavingImport(true);
    setImportMessage(null);
    let attempt: RetainedAutomationAttempt | null = null;
    let wasDispatched = false;
    try {
      attempt = await automationAttempt(
        importAttemptRef,
        signature,
        'automation-import-save',
        attemptNamespace,
        'import-save',
        { index_id: indexId, payload },
      );
      const claimIsCurrent = (
        generation === importMutationGenerationRef.current
        && selectedIndexRef.current === indexId
        && draftScopeIndexIdRef.current === indexId
        && importDraftRevisionRef.current === draftRevision
        && editableImportSignature(importDraftStateRef.current) === draftSignature
        && importAutomationSnapshotSignature(savedImportDraftRef.current) === baselineSignature
      );
      if (!claimIsCurrent) {
        releaseDefinitiveAttempt(importAttemptRef, signature);
        if (
          generation === importMutationGenerationRef.current
          && selectedIndexRef.current === indexId
        ) {
          setImportMessage({
            type: 'info',
            text: 'Settings changed before saving began. Review them and save again.',
          });
        }
        return false;
      }
      wasDispatched = true;
      const saved = await apiService.putImportAutomation(indexId, payload, attempt.idempotencyKey);
      releaseDefinitiveAttempt(importAttemptRef, signature);
      if (
        generation !== importMutationGenerationRef.current
        || selectedIndexRef.current !== indexId
      ) return true;
      const next = toImportAutomationDraft(saved);
      pendingImportReconciliationRef.current.delete(indexId);
      provisioningAttemptsRef.current.import.delete(indexId);
      savedImportDraftRef.current = next;
      importDraftStateRef.current = next;
      importBaselineLoadedRef.current.add(indexId);
      setSavedImportDraft(next);
      setImportDraftState(next);
      commitDirtyDrafts(indexId, { importDraft: null });
      setImportMessage(getAutomationMutationMessage(
        saved,
        successMessage,
        failureMessage,
      ));
      return true;
    } catch (error) {
      releaseDefinitiveAttempt(
        importAttemptRef,
        signature,
        wasDispatched ? error : undefined,
      );
      if (
        generation === importMutationGenerationRef.current
        && selectedIndexRef.current === indexId
      ) {
        if (!wasDispatched) {
          setImportMessage({
            type: 'error',
            text: getApiErrorMessage(error, 'Durable retry protection is unavailable.'),
          });
        } else if (isRetryableStreamRequestError(error)) {
          await reconcileAmbiguousImportMutation(indexId, {
            attemptChannel: 'save',
            attemptSignature: signature,
            baselineSignature,
            expectedSignature: expectedReconcileSignature,
            successMessage,
            failureMessage,
          });
        } else {
          setImportMessage({
            type: 'error',
            text: getApiErrorMessage(error, 'Failed to save import automation.'),
          });
          await observeDefinitiveImportMutationError(indexId, {
            baselineSignature,
            expectedSignature: expectedReconcileSignature,
            successMessage,
            failureMessage,
          });
        }
      }
      return false;
    } finally {
      if (generation === importMutationGenerationRef.current) {
        isSavingImportRef.current = false;
        setIsSavingImport(false);
      }
    }
  }, [
    attemptNamespace,
    commitDirtyDrafts,
    getImportValidationError,
    observeDefinitiveImportMutationError,
    reconcileAmbiguousImportMutation,
  ]);

  const saveExport = useCallback(async (): Promise<boolean> => {
    const indexId = selectedIndexRef.current;
    const draft = exportDraftStateRef.current;
    const savedDraft = savedExportDraftRef.current;
    if (
      !indexId
      || draftScopeIndexIdRef.current !== indexId
      || isSavingExportRef.current
    ) return false;
    if (savedDraft?.runtime_status === 'provisioning') {
      setExportMessage({
        type: 'info',
        text: 'Wait for the current scheduler update to finish before saving again.',
      });
      return false;
    }
    const pendingReconciliation = pendingExportReconciliationRef.current.get(indexId);
    if (pendingReconciliation && pendingReconciliation.attemptChannel !== 'save') {
      setExportMessage({
        type: 'info',
        text: 'Wait for the pending export update to finish before starting another change.',
      });
      return false;
    }
    const validationError = getExportValidationError(draft);
    if (validationError) {
      setExportMessage({ type: 'error', text: validationError });
      return false;
    }
    const baselineSignature = exportAutomationSnapshotSignature(savedDraft);
    const successMessage = 'Export automation saved.';
    const failureMessage = 'Export automation setup failed. The automation is not active.';
    const destinationSubpath = normalizeExportDestinationSubpath(
      draft.destination_subpath,
    );
    const payload = buildExportMutationPayload(draft);
    const expectedReconcileSignature = editableExportSignature({
      ...draft,
      paused: payload.paused,
      destination_subpath: destinationSubpath.value,
    });
    const signature = JSON.stringify([indexId, 'save', payload]);
    const draftSignature = editableExportSignature(draft);
    const draftRevision = exportDraftRevisionRef.current;
    if (pendingReconciliation && pendingReconciliation.attemptSignature !== signature) {
      setExportMessage({
        type: 'info',
        text: 'Wait for the pending export update to finish before starting another change.',
      });
      return false;
    }
    exportLoadGenerationRef.current += 1;
    const generation = ++exportMutationGenerationRef.current;
    isSavingExportRef.current = true;
    setIsSavingExport(true);
    setExportMessage(null);
    let attempt: RetainedAutomationAttempt | null = null;
    let wasDispatched = false;
    try {
      attempt = await automationAttempt(
        exportAttemptRef,
        signature,
        'automation-export-save',
        attemptNamespace,
        'export-save',
        { index_id: indexId, payload },
      );
      const claimIsCurrent = (
        generation === exportMutationGenerationRef.current
        && selectedIndexRef.current === indexId
        && draftScopeIndexIdRef.current === indexId
        && exportDraftRevisionRef.current === draftRevision
        && editableExportSignature(exportDraftStateRef.current) === draftSignature
        && exportAutomationSnapshotSignature(savedExportDraftRef.current) === baselineSignature
      );
      if (!claimIsCurrent) {
        releaseDefinitiveAttempt(exportAttemptRef, signature);
        if (
          generation === exportMutationGenerationRef.current
          && selectedIndexRef.current === indexId
        ) {
          setExportMessage({
            type: 'info',
            text: 'Settings changed before saving began. Review them and save again.',
          });
        }
        return false;
      }
      wasDispatched = true;
      const saved = await apiService.putExportAutomation(indexId, payload, attempt.idempotencyKey);
      releaseDefinitiveAttempt(exportAttemptRef, signature);
      if (
        generation !== exportMutationGenerationRef.current
        || selectedIndexRef.current !== indexId
      ) return true;
      const next = toExportAutomationDraft(saved);
      pendingExportReconciliationRef.current.delete(indexId);
      provisioningAttemptsRef.current.export.delete(indexId);
      savedExportDraftRef.current = next;
      exportDraftStateRef.current = next;
      exportBaselineLoadedRef.current.add(indexId);
      setSavedExportDraft(next);
      setExportDraftState(next);
      commitDirtyDrafts(indexId, { exportDraft: null });
      setExportMessage(getAutomationMutationMessage(
        saved,
        successMessage,
        failureMessage,
      ));
      return true;
    } catch (error) {
      releaseDefinitiveAttempt(
        exportAttemptRef,
        signature,
        wasDispatched ? error : undefined,
      );
      if (
        generation === exportMutationGenerationRef.current
        && selectedIndexRef.current === indexId
      ) {
        if (!wasDispatched) {
          setExportMessage({
            type: 'error',
            text: getApiErrorMessage(error, 'Durable retry protection is unavailable.'),
          });
        } else if (isRetryableStreamRequestError(error)) {
          await reconcileAmbiguousExportMutation(indexId, {
            attemptChannel: 'save',
            attemptSignature: signature,
            baselineSignature,
            expectedSignature: expectedReconcileSignature,
            successMessage,
            failureMessage,
          });
        } else {
          setExportMessage({
            type: 'error',
            text: getApiErrorMessage(error, 'Failed to save export automation.'),
          });
          await observeDefinitiveExportMutationError(indexId, {
            baselineSignature,
            expectedSignature: expectedReconcileSignature,
            successMessage,
            failureMessage,
          });
        }
      }
      return false;
    } finally {
      if (generation === exportMutationGenerationRef.current) {
        isSavingExportRef.current = false;
        setIsSavingExport(false);
      }
    }
  }, [
    attemptNamespace,
    commitDirtyDrafts,
    getExportValidationError,
    observeDefinitiveExportMutationError,
    reconcileAmbiguousExportMutation,
  ]);

  const toggleImportPause = useCallback(async (): Promise<boolean> => {
    const indexId = selectedIndexRef.current;
    const savedDraft = savedImportDraftRef.current;
    if (
      !indexId
      || draftScopeIndexIdRef.current !== indexId
      || !savedDraft
      || !savedDraft.enabled
    ) return false;
    if (savedDraft.runtime_status === 'provisioning') {
      setImportMessage({
        type: 'info',
        text: 'Wait for the current scheduler update to finish before changing status.',
      });
      return false;
    }
    const pendingReconciliation = pendingImportReconciliationRef.current.get(indexId);
    if (pendingReconciliation && pendingReconciliation.attemptChannel !== 'status') {
      setImportMessage({
        type: 'info',
        text: 'Wait for the pending import update to finish before starting another change.',
      });
      return false;
    }
    if (importDirtyRef.current) return false;
    if (isSavingImportRef.current) return false;
    const currentlyPaused = isAutomationPaused(savedDraft);
    if (currentlyPaused) {
      const validationError = getImportValidationError(savedDraft);
      if (validationError) {
        setImportMessage({ type: 'error', text: validationError });
        return false;
      }
    }
    const action = currentlyPaused ? 'resume' : 'pause';
    const baselineSignature = importAutomationSnapshotSignature(savedDraft);
    const successMessage = currentlyPaused
      ? 'Import automation resumed.'
      : 'Import automation paused.';
    const failureMessage = 'Scheduler update failed. Import automation is not active.';
    const expectedReconcileSignature = editableImportSignature({
      ...savedDraft,
      paused: !currentlyPaused,
    });
    const signature = JSON.stringify([indexId, action]);
    const draftSignature = editableImportSignature(importDraftStateRef.current);
    const draftRevision = importDraftRevisionRef.current;
    if (pendingReconciliation && pendingReconciliation.attemptSignature !== signature) {
      setImportMessage({
        type: 'info',
        text: 'Wait for the pending import update to finish before starting another change.',
      });
      return false;
    }
    importLoadGenerationRef.current += 1;
    const generation = ++importMutationGenerationRef.current;
    isSavingImportRef.current = true;
    setIsSavingImport(true);
    setImportMessage(null);
    let attempt: RetainedAutomationAttempt | null = null;
    let wasDispatched = false;
    try {
      attempt = await automationAttempt(
        importPauseAttemptRef,
        signature,
        `automation-import-${action}`,
        attemptNamespace,
        'import-status',
        { index_id: indexId, action },
      );
      const claimIsCurrent = (
        generation === importMutationGenerationRef.current
        && selectedIndexRef.current === indexId
        && draftScopeIndexIdRef.current === indexId
        && importDraftRevisionRef.current === draftRevision
        && editableImportSignature(importDraftStateRef.current) === draftSignature
        && importAutomationSnapshotSignature(savedImportDraftRef.current) === baselineSignature
        && !importDirtyRef.current
      );
      if (!claimIsCurrent) {
        releaseDefinitiveAttempt(importPauseAttemptRef, signature);
        return false;
      }
      wasDispatched = true;
      const saved = currentlyPaused
        ? await apiService.resumeImportAutomation(indexId, attempt.idempotencyKey)
        : await apiService.pauseImportAutomation(indexId, attempt.idempotencyKey);
      if (!saved) {
        releaseDefinitiveAttempt(importPauseAttemptRef, signature);
        pendingImportReconciliationRef.current.delete(indexId);
        if (
          generation === importMutationGenerationRef.current
          && selectedIndexRef.current === indexId
        ) {
          setImportMessage({
            type: 'error',
            text: 'Import automation no longer exists. Refresh before retrying.',
          });
        }
        return false;
      }
      releaseDefinitiveAttempt(importPauseAttemptRef, signature);
      if (
        generation !== importMutationGenerationRef.current
        || selectedIndexRef.current !== indexId
      ) return true;
      const next = toImportAutomationDraft(saved);
      pendingImportReconciliationRef.current.delete(indexId);
      provisioningAttemptsRef.current.import.delete(indexId);
      savedImportDraftRef.current = next;
      importDraftStateRef.current = next;
      importBaselineLoadedRef.current.add(indexId);
      setSavedImportDraft(next);
      setImportDraftState(next);
      commitDirtyDrafts(indexId, { importDraft: null });
      setImportMessage(getAutomationMutationMessage(
        saved,
        successMessage,
        failureMessage,
      ));
      return true;
    } catch (error) {
      releaseDefinitiveAttempt(
        importPauseAttemptRef,
        signature,
        wasDispatched ? error : undefined,
      );
      if (
        generation === importMutationGenerationRef.current
        && selectedIndexRef.current === indexId
      ) {
        if (!wasDispatched) {
          setImportMessage({
            type: 'error',
            text: getApiErrorMessage(error, 'Durable retry protection is unavailable.'),
          });
        } else if (isRetryableStreamRequestError(error)) {
          await reconcileAmbiguousImportMutation(indexId, {
            attemptChannel: 'status',
            attemptSignature: signature,
            baselineSignature,
            expectedSignature: expectedReconcileSignature,
            successMessage,
            failureMessage,
          });
        } else {
          setImportMessage({
            type: 'error',
            text: getApiErrorMessage(error, 'Failed to update import automation status.'),
          });
          await observeDefinitiveImportMutationError(indexId, {
            baselineSignature,
            expectedSignature: expectedReconcileSignature,
            successMessage,
            failureMessage,
          });
        }
      }
      return false;
    } finally {
      if (generation === importMutationGenerationRef.current) {
        isSavingImportRef.current = false;
        setIsSavingImport(false);
      }
    }
  }, [
    attemptNamespace,
    commitDirtyDrafts,
    getImportValidationError,
    observeDefinitiveImportMutationError,
    reconcileAmbiguousImportMutation,
  ]);

  const toggleExportPause = useCallback(async (): Promise<boolean> => {
    const indexId = selectedIndexRef.current;
    const savedDraft = savedExportDraftRef.current;
    if (
      !indexId
      || draftScopeIndexIdRef.current !== indexId
      || !savedDraft
      || !savedDraft.enabled
    ) return false;
    if (savedDraft.runtime_status === 'provisioning') {
      setExportMessage({
        type: 'info',
        text: 'Wait for the current scheduler update to finish before changing status.',
      });
      return false;
    }
    const pendingReconciliation = pendingExportReconciliationRef.current.get(indexId);
    if (pendingReconciliation && pendingReconciliation.attemptChannel !== 'status') {
      setExportMessage({
        type: 'info',
        text: 'Wait for the pending export update to finish before starting another change.',
      });
      return false;
    }
    if (exportDirtyRef.current) return false;
    if (isSavingExportRef.current) return false;
    const currentlyPaused = isAutomationPaused(savedDraft);
    if (currentlyPaused) {
      const validationError = getExportValidationError(savedDraft);
      if (validationError) {
        setExportMessage({ type: 'error', text: validationError });
        return false;
      }
    }
    const action = currentlyPaused ? 'resume' : 'pause';
    const baselineSignature = exportAutomationSnapshotSignature(savedDraft);
    const successMessage = currentlyPaused
      ? 'Export automation resumed.'
      : 'Export automation paused.';
    const failureMessage = 'Export automation setup failed. The automation is not active.';
    const expectedReconcileSignature = editableExportSignature({
      ...savedDraft,
      paused: !currentlyPaused,
    });
    const signature = JSON.stringify([indexId, action]);
    const draftSignature = editableExportSignature(exportDraftStateRef.current);
    const draftRevision = exportDraftRevisionRef.current;
    if (pendingReconciliation && pendingReconciliation.attemptSignature !== signature) {
      setExportMessage({
        type: 'info',
        text: 'Wait for the pending export update to finish before starting another change.',
      });
      return false;
    }
    exportLoadGenerationRef.current += 1;
    const generation = ++exportMutationGenerationRef.current;
    isSavingExportRef.current = true;
    setIsSavingExport(true);
    setExportMessage(null);
    let attempt: RetainedAutomationAttempt | null = null;
    let wasDispatched = false;
    try {
      attempt = await automationAttempt(
        exportPauseAttemptRef,
        signature,
        `automation-export-${action}`,
        attemptNamespace,
        'export-status',
        { index_id: indexId, action },
      );
      const claimIsCurrent = (
        generation === exportMutationGenerationRef.current
        && selectedIndexRef.current === indexId
        && draftScopeIndexIdRef.current === indexId
        && exportDraftRevisionRef.current === draftRevision
        && editableExportSignature(exportDraftStateRef.current) === draftSignature
        && exportAutomationSnapshotSignature(savedExportDraftRef.current) === baselineSignature
        && !exportDirtyRef.current
      );
      if (!claimIsCurrent) {
        releaseDefinitiveAttempt(exportPauseAttemptRef, signature);
        return false;
      }
      wasDispatched = true;
      const saved = currentlyPaused
        ? await apiService.resumeExportAutomation(indexId, attempt.idempotencyKey)
        : await apiService.pauseExportAutomation(indexId, attempt.idempotencyKey);
      if (!saved) {
        releaseDefinitiveAttempt(exportPauseAttemptRef, signature);
        pendingExportReconciliationRef.current.delete(indexId);
        if (
          generation === exportMutationGenerationRef.current
          && selectedIndexRef.current === indexId
        ) {
          setExportMessage({
            type: 'error',
            text: 'Export automation no longer exists. Refresh before retrying.',
          });
        }
        return false;
      }
      releaseDefinitiveAttempt(exportPauseAttemptRef, signature);
      if (
        generation !== exportMutationGenerationRef.current
        || selectedIndexRef.current !== indexId
      ) return true;
      const next = toExportAutomationDraft(saved);
      pendingExportReconciliationRef.current.delete(indexId);
      provisioningAttemptsRef.current.export.delete(indexId);
      savedExportDraftRef.current = next;
      exportDraftStateRef.current = next;
      exportBaselineLoadedRef.current.add(indexId);
      setSavedExportDraft(next);
      setExportDraftState(next);
      commitDirtyDrafts(indexId, { exportDraft: null });
      setExportMessage(getAutomationMutationMessage(
        saved,
        successMessage,
        failureMessage,
      ));
      return true;
    } catch (error) {
      releaseDefinitiveAttempt(
        exportPauseAttemptRef,
        signature,
        wasDispatched ? error : undefined,
      );
      if (
        generation === exportMutationGenerationRef.current
        && selectedIndexRef.current === indexId
      ) {
        if (!wasDispatched) {
          setExportMessage({
            type: 'error',
            text: getApiErrorMessage(error, 'Durable retry protection is unavailable.'),
          });
        } else if (isRetryableStreamRequestError(error)) {
          await reconcileAmbiguousExportMutation(indexId, {
            attemptChannel: 'status',
            attemptSignature: signature,
            baselineSignature,
            expectedSignature: expectedReconcileSignature,
            successMessage,
            failureMessage,
          });
        } else {
          setExportMessage({
            type: 'error',
            text: getApiErrorMessage(error, 'Failed to update export automation status.'),
          });
          await observeDefinitiveExportMutationError(indexId, {
            baselineSignature,
            expectedSignature: expectedReconcileSignature,
            successMessage,
            failureMessage,
          });
        }
      }
      return false;
    } finally {
      if (generation === exportMutationGenerationRef.current) {
        isSavingExportRef.current = false;
        setIsSavingExport(false);
      }
    }
  }, [
    attemptNamespace,
    commitDirtyDrafts,
    getExportValidationError,
    observeDefinitiveExportMutationError,
    reconcileAmbiguousExportMutation,
  ]);

  const resetImportDraft = useCallback(() => {
    const next = savedImportDraft || createDefaultImportAutomationDraft();
    importDraftRevisionRef.current += 1;
    importDraftStateRef.current = next;
    setImportDraftState(next);
    const indexId = draftScopeIndexIdRef.current;
    if (indexId) commitDirtyDrafts(indexId, { importDraft: null });
    setImportMessage(null);
  }, [commitDirtyDrafts, savedImportDraft]);

  const resetExportDraft = useCallback(() => {
    const next = savedExportDraft || createDefaultExportAutomationDraft();
    exportDraftRevisionRef.current += 1;
    exportDraftStateRef.current = next;
    setExportDraftState(next);
    const indexId = draftScopeIndexIdRef.current;
    if (indexId) commitDirtyDrafts(indexId, { exportDraft: null });
    setExportMessage(null);
  }, [commitDirtyDrafts, savedExportDraft]);

  const referencesError = useMemo(() => {
    const errors = [
      referenceErrors.indexes,
      referenceErrors.connectors,
      ...(loadImportReferences
        ? [engineCatalog.error]
        : []),
    ].filter((message): message is string => Boolean(message));
    return errors.length > 0 ? errors.join(' ') : null;
  }, [engineCatalog.error, loadImportReferences, referenceErrors]);

  const isSwitchingIndex = Boolean(selectedIndexKey) && !draftScopeMatchesSelection;
  const visibleImportDraft = draftScopeMatchesSelection
    ? importDraftState
    : createDefaultImportAutomationDraft();
  const visibleExportDraft = draftScopeMatchesSelection
    ? exportDraftState
    : createDefaultExportAutomationDraft();

  return {
    eligibleIndexes,
    selectedIndex,
    importConnectors,
    exportConnectors,
    prompts: engineCatalog.prompts,
    importDraft: visibleImportDraft,
    exportDraft: visibleExportDraft,
    savedImportDraft: draftScopeMatchesSelection ? savedImportDraft : null,
    savedExportDraft: draftScopeMatchesSelection ? savedExportDraft : null,
    setImportDraft,
    setExportDraft,
    importDirty: draftScopeMatchesSelection ? importDirty : false,
    exportDirty: draftScopeMatchesSelection ? exportDirty : false,
    importValidationError: draftScopeMatchesSelection
      ? getImportValidationError(importDraftState)
      : null,
    exportValidationError: draftScopeMatchesSelection
      ? getExportValidationError(exportDraftState)
      : null,
    pendingImportMutation,
    pendingExportMutation,
    isLoadingReferences: (
      isLoadingIndexes
      || isLoadingConnectors
      || (loadImportReferences && engineCatalog.isLoading)
    ),
    isLoadingIndexes,
    isLoadingConnectors,
    isLoadingEngines: loadImportReferences && engineCatalog.isLoading,
    isLoadingImport: isLoadingImport || isSwitchingIndex,
    isLoadingExport: isLoadingExport || isSwitchingIndex,
    hasLoadedImport: Boolean(
      selectedIndexKey && importBaselineLoadedRef.current.has(selectedIndexKey),
    ),
    hasLoadedExport: Boolean(
      selectedIndexKey && exportBaselineLoadedRef.current.has(selectedIndexKey),
    ),
    isSavingImport,
    isSavingExport,
    indexesError: referenceErrors.indexes,
    connectorsError: referenceErrors.connectors,
    enginesError: engineCatalog.error,
    referencesError,
    importError: draftScopeMatchesSelection ? importError : null,
    exportError: draftScopeMatchesSelection ? exportError : null,
    importMessage: draftScopeMatchesSelection ? importMessage : null,
    exportMessage: draftScopeMatchesSelection ? exportMessage : null,
    refresh,
    retryImport,
    retryExport,
    saveImport,
    saveExport,
    toggleImportPause,
    toggleExportPause,
    resetImportDraft,
    resetExportDraft,
  };
};
